<template>
    <v-card>
        <v-card-title>
            Create Communication Logs

            <v-spacer></v-spacer>

            <btn label="Create Communication Log" :icon="icons.mdiEmailArrowRight"
                :disabled="!selected.length || !isFiltersSet" color="success" @click="openCommModal"></btn>
        </v-card-title>

        <v-expand-transition>
            <div style="height: 60px">
                <alert color="primary" class="mx-5">
                    Communication logs can only be created from signed notes dated today.
                </alert>
            </div>
        </v-expand-transition>

        <v-expand-transition>
            <div v-if="!isFiltersSet" style="height: 60px">
                <alert class="mx-5">
                    Facility and Visit Date must be selected to create a communication log.
                </alert>
            </div>
        </v-expand-transition>

        <v-data-table v-model="selected" :headers="headers" :items="paginatedEncounters" item-key="id"
            :page.sync="pagination.page" :items-per-page="pagination.itemsPerPage"
            hide-default-footer show-select class="has-pagination"
            @page-count="pagination.pageCount = $event"
            @update:sort-by="onSortChange($event, 'by')" @update:sort-desc="onSortChange($event, 'desc')">
            <template #top>
                <v-row class="mx-4 mt-2 pb-1">
                    <select-box v-model="search.facility" :items="facilities" label="Facility" class="col-sm-9 px-1"
                        :class="!search.facility && 'required-field'"></select-box>
                    <date-picker v-model="search.visit_date" label="Visit Date" :max="dos_default_max" :disabled="!isSuperUser"
                        class="col-sm-3 px-1" :class="!search.visit_date && 'required-field'"></date-picker>
                </v-row>

                <v-row v-if="encountersLength >= 1000" class="justify-center mx-4 my-0 py-0">
                    <v-col class="col-sm-12">
                        <alert>
                            {{ filterMessage }}
                        </alert>
                    </v-col>
                </v-row>

                <!-- icon help -->
                <v-expand-transition>
                    <v-row v-show="showHelp" class="help-icons">
                        <div class="secondary--text">Icon Help:</div>
                        <icon-value :icon="icons.mdiDecagramOutline" color="secondary"> Validated </icon-value>
                        <icon-value :icon="icons.mdiEmailOutline" color="secondary"> Log Status </icon-value>
                        <icon-value :icon="icons.mdiCloudOutline" color="secondary"> Synced </icon-value>
                    </v-row>
                </v-expand-transition>
            </template>

            <!-- header overrides -->
            <template #header.data-table-select>
                <v-checkbox v-model="selectAllStatus" :indeterminate="selectAllIndeterminate"
                    :off-icon="icons.mdiCheckboxMultipleBlankOutline" :on-icon="icons.mdiCheckboxMultipleMarked"
                    :indeterminate-icon="icons.mdiMinusBoxMultiple" hide-details color="secondary"
                    :disabled="!isFiltersSet || !paginatedEncounters.length" @click="selectAllToggle"></v-checkbox>
            </template>
            <template #header.is_validated>
                <v-icon>{{ icons.mdiDecagramOutline }}</v-icon>
            </template>
            <template #header.communication_log.log_status>
                <v-icon>{{ icons.mdiEmailOutline }}</v-icon>
            </template>
            <template #header.is_synced>
                <v-icon>{{ icons.mdiCloudOutline }}</v-icon>
            </template>
            <template #header.actions>
                <v-icon color="warning" @click="showHelp = !showHelp">
                    {{ icons.mdiInformationOutline }}
                </v-icon>
            </template>

            <!-- table data overrides -->
            <template #item.data-table-select="{ item }">
                <v-checkbox v-model="selected" :value="item" hide-details color="secondary"
                    :disabled="!isFiltersSet || !item.is_validated" @click.stop="updateSelectStatus"></v-checkbox>
            </template>

            <!-- visit date -->
            <template #item.visit_date="{ item }">
                {{ $date(item.visit_date).format('MM/DD/YYYY') }}
            </template>

            <!-- first name -->
            <template #item.first_name="{ item }">
                <div class="table-no-wrap">
                    {{ item.first_name }}
                </div>
            </template>

            <!-- last name -->
            <template #item.last_name="{ item }">
                <div class="table-no-wrap">
                    {{ item.last_name }}
                </div>
            </template>

            <!-- facility -->
            <template #item.place_of_service_id="{ item }">
                <div class="table-no-wrap">
                    {{ getFacilityName(item.place_of_service_id) }}
                </div>
            </template>

            <!-- visit type -->
            <template #item.visit_type="{ item }">
                <span v-if="item.visit_type" class="visit-type" :class="item.visit_type.toLowerCase()">
                    {{ item.visit_type === 'Follow-up' ? 'F/U' : item.visit_type }}
                </span>
            </template>

            <!-- Validated -->
            <template #item.is_validated="{ item }">
                <v-icon :color="item.is_validated || item.is_signed ? 'success' : 'error'">
                    {{ item.is_validated || item.is_signed ? icons.mdiCheckDecagram : icons.mdiCloseCircle }}
                </v-icon>
            </template>

            <!-- Synced -->
            <template #item.is_synced="{ item }">
                <v-icon :color="item.is_synced ? 'success' : 'error'">
                    {{ item.is_synced ? icons.mdiCloudCheckVariant : icons.mdiCloseCircle }}
                </v-icon>
            </template>

            <!-- Log status -->
            <template #item.communication_log.log_status="{ item }">
                <v-icon :color="logStatusColor(item.communication_log)">
                    {{ logStatusIcon(item.communication_log) }}
                </v-icon>
            </template>

            <template v-slot:no-data>
                <v-progress-circular v-if="loading" indeterminate color="primary"
                    class="mx-auto my-5"></v-progress-circular>
                <span v-else-if="!isFiltersSet">Please select a Facility and Visit Date to view records.</span>
                <span v-else>No data available</span>
            </template>
        </v-data-table>

        <!-- pagination -->
        <pagination :page.sync="page" :items-per-page.sync="pagination.itemsPerPage" :page-count="pageCount">
        </pagination>

        <v-card-actions></v-card-actions>

        <!-- Create Communication Log Modal -->
        <v-dialog v-model="commModalState" width="650" persistent>
            <v-card>
                <v-form v-model="formValid">
                    <v-card-title class="pb-0">
                        {{ isSelectedNewLog ? 'Create Communication Log' : 'Resend Communication Log' }}

                        <v-spacer></v-spacer>

                        <!-- reset -->
                        <btn label="Reset Values" color="secondary" :icon="icons.mdiUndoVariant" @click="openCommModal">
                        </btn>
                    </v-card-title>

                    <NoteSection>
                        <text-field :value="facility.title" label="Facility" class="col-sm-8 mt-3"
                            disabled></text-field>
                        <text-field :value="$date(search.visit_date).format('MM/DD/YYYY')" label="Visit Date"
                            class="col-sm-4 mt-3" disabled></text-field>
                        <text-area :value="communicationLog.patients" :no-length="true"
                            label="Patients Included in Communication Log" class="col-sm-12" rows="1" readonly
                            no-click></text-area>
                        <text-field v-model="communicationLog.password" label="Password for Communication Log"
                            class="col-sm-7" readonly password
                            :password-visible.sync="communicationLog.isPasswordVisible"
                            @focus="$event.target.blur()"></text-field>
                        <btn label="Generate New Password" color="warning" class="col-sm-5" :icon="icons.mdiLockReset"
                            @click="generateNewPassword"></btn>
                        <v-combobox v-model="communicationLog.recipients" :items="communicationLog.emailOptions"
                            :rules="computedRules" :delimiters="[',', ' ']" label="Email Recipients" class="col-sm-12"
                            counter="10" hide-selected outlined multiple chips deletable-chips
                            @change="recipientsChanged" @blur="commitPendingEmailEntry"
                            @update:search-input="emailInput = $event" ref="emailCombo"></v-combobox>

                        <div class="row-container col-sm-12">
                            <v-combobox v-model="selectedCountryCode" label="Country code" :items="countryCodes"
                                :item-text="'id'" :item-value="'id'" class="col-sm-3 country-code" :height="72" filled
                                outlined readonly></v-combobox>

                            <v-combobox ref="mobileNumbersCombobox" v-model="communicationLog.mobileNumbers"
                                :rules="mobileNumberRules" :delimiters="[',', ' ']" label="Sms Recipients"
                                class="col-sm-9" counter="10" hide-selected outlined chips deletable-chips
                                multiple></v-combobox>
                        </div>
                        <btn :label="sendPasswordLabel" color="warning" class="col-sm-12"
                            :disabled="sendSmsPasswordBtnDisabled" :icon="icons.mdiSendCircle"
                            @click="sendPasswordThroughSms"></btn>
                    </NoteSection>

                    <alert class="mx-5">
                        <p>
                            Are you sure you wish to create the communication log and email it to the listed recipients
                            at {{ facility.title }}?
                        </p>
                        Warning: This operation cannot be undone!
                    </alert>

                    <v-card-actions>
                        <!-- cancel -->
                        <btn label="Cancel" color="secondary" :icon="icons.mdiCancel" @click="
                            commModalState = false
                        communicationLog.isPasswordVisible = false
                        communicationLog.mobileNumbers = []
                            "></btn>

                        <v-spacer></v-spacer>

                        <!-- send -->
                        <btn :label="isSelectedNewLog ? 'Create Log' : 'Resend Log'" color="error"
                            :icon="icons.mdiEmailArrowRight" :disabled="!formValid" offline-disable
                            @click="createCommunicationLog"></btn>
                    </v-card-actions>
                </v-form>
            </v-card>
        </v-dialog>

        <!-- Log Detail Modal -->
        <v-dialog v-model="detailModalState" width="550" persistent>
            <v-card>
                <v-card-title class="pb-0"> Communication Log Details </v-card-title>

                <NoteSection>
                    <text-field :value="logDetails.log_status" label="Communication Log Status" class="col-sm-6 mt-3"
                        readonly @focus="$event.target.blur()"></text-field>
                    <text-field :value="$date(logDetails.created).format('MM/DD/YYYY hh:mm A')" label="Sent On"
                        class="col-sm-6 mt-3" readonly @focus="$event.target.blur()"></text-field>
                    <text-field :value="$store.getters['facilities/getById'](logDetails.place_of_service_id).title"
                        label="Facility" class="col-sm-8" readonly @focus="$event.target.blur()"></text-field>
                    <text-field :value="$date(logDetails.visit_date).format('MM/DD/YYYY')" label="Visit Date"
                        class="col-sm-4" readonly @focus="$event.target.blur()"></text-field>
                    <text-field :value="logDetails.password" label="Password for Communication Log" class="col-sm-12"
                        readonly @focus="$event.target.blur()"></text-field>
                    <text-area :value="logDetails.contact_email || ' '" :no-length="true" label="Email Recipients"
                        class="col-sm-12" readonly></text-area>
                    <div class="row-container col-sm-12">
                        <v-combobox v-model="selectedCountryCode" label="Country code" :items="countryCodes"
                            :item-text="'id'" :item-value="'id'" class="col-sm-3 country-code" :height="72" filled
                            outlined readonly></v-combobox>

                        <v-combobox ref="mobileNumbersComboboxDetail" v-model="communicationLog.mobileNumbers"
                            :rules="mobileNumberRules" :delimiters="[',', ' ']" label="Sms Recipients" class="col-sm-9"
                            counter="10" hide-selected outlined chips deletable-chips multiple></v-combobox>
                    </div>
                    <btn :label="sendPasswordLabel" color="warning" class="col-sm-12"
                        :disabled="sendSmsPasswordBtnDisabled" :icon="icons.mdiSendCircle"
                        @click="sendPasswordThroughSms"></btn>
                </NoteSection>

                <v-card-actions>
                    <!-- cancel -->
                    <btn label="Close" color="secondary" :icon="icons.mdiClose" @click="
                        detailModalState = false
                    communicationLog.mobileNumbers = []
                        "></btn>
                    <v-spacer></v-spacer>
                    <!-- resend -->
                    <btn label="Resend Log" color="error" :icon="icons.mdiEmailArrowRight"
                        @click="resendCommunicationLog">
                    </btn>
                </v-card-actions>
            </v-card>
        </v-dialog>
    </v-card>
</template>

<script>
import '@/components/patients/primitives'
import CommunicationLog from '@/mixins/CommunicationLog'
import {
    mdiCancel,
    mdiCheckboxMultipleBlankOutline,
    mdiCheckboxMultipleMarked,
    mdiCheckDecagram,
    mdiChevronDown,
    mdiClose,
    mdiCloseCircle,
    mdiCloudCheckVariant,
    mdiCloudOutline,
    mdiDecagramOutline,
    mdiEmailAlert,
    mdiEmailArrowRight,
    mdiEmailCheck,
    mdiEmailOutline,
    mdiInformationOutline,
    mdiLockReset,
    mdiMinusBoxMultiple,
    mdiSendCircle,
    mdiUndoVariant,
} from '@mdi/js'
import compact from 'lodash/compact'
import { mapGetters, mapState } from 'vuex'

const initialSearch = {
    visit_date: null,
    visit_date_us: null,
    facility: null,
}

const initialState = {
    by: 'visit_date',
    desc: true,
}


export default {
    mixins: [CommunicationLog],
    data() {
        return {
            dos_default_max: this.$date().format('YYYY-MM-DD'),
            search: { ...initialSearch },
            sort: { ...initialState },
            userId: this.$scribeProvider.id ?? this.$authUser.userId(),
            logDetails: {},
            logDetailEncounterId: null,
            commModalState: false,
            detailModalState: false,
            formValid: false,
            emailInput: '',
            isSelectedNewLog: true,
            communicationLog: {
                password: null,
                emailOptions: [],
                recipients: [],
                patients: null,
                isPasswordVisible: false,
                mobileNumbers: [],
            },
            countryCodes: this.$countryCodes.transformedCountryCodes,
            selectedCountryCode: '(+1) US',
            sendSmsPasswordBtnDisabled: false,
            sendPasswordLabel: 'Send Password',
            selected: [],
            selectAllStatus: false,
            selectAllIndeterminate: false,
            icons: {
                mdiCloseCircle,
                mdiCloudCheckVariant,
                mdiUndoVariant,
                mdiCheckDecagram,
                mdiClose,
                mdiDecagramOutline,
                mdiCloudOutline,
                mdiInformationOutline,
                mdiChevronDown,
                mdiEmailArrowRight,
                mdiLockReset,
                mdiSendCircle,
                mdiCancel,
                mdiCheckboxMultipleBlankOutline,
                mdiCheckboxMultipleMarked,
                mdiMinusBoxMultiple,
                mdiEmailOutline,
                mdiEmailAlert,
                mdiEmailCheck,
                mdiCloseCircle,
            },
            showHelp: false,
            headers: [
                {
                    text: 'First',
                    value: 'first_name',
                    align: 'name',
                },
                {
                    text: 'Last',
                    value: 'last_name',
                    align: 'name',
                },
                {
                    text: 'Visit Date',
                    value: 'visit_date',
                },
                {
                    text: 'Facility',
                    value: 'place_of_service_id',
                    align: 'facility',
                },
                {
                    text: 'Type',
                    value: 'visit_type',
                    align: 'xsmall',
                },
                {
                    text: 'Validated',
                    value: 'is_validated',
                    align: 'xsmall',
                },
                {
                    text: 'Log Status',
                    value: 'communication_log.log_status',
                    align: 'xsmall',
                },
                {
                    text: 'Synced',
                    value: 'is_synced',
                    align: 'xsmall',
                },
                {
                    value: 'dob',
                    align: 'hide',
                },
                {
                    value: 'gender',
                    align: 'hide',
                },
                {
                    value: 'ssn_last_4',
                    align: 'hide',
                },
                {
                    text: '',
                    value: 'actions',
                    align: 'xsmall',
                    sortable: false,
                },
            ],
            expanded: [],
            pagination: {
                page: 1,
                pageCount: 0,
                itemsPerPage: 10,
            },
            debounceTimer: null,
            encountersLength: 0,
            filteredEncounters: [],
            loading: false,
        }
    },
    beforeDestroy() {
        clearTimeout(this.debounceTimer)
        this.debounceTimer = null
        this.filteredEncounters = []
    },
    computed: {
        ...mapGetters('baseData', ['query']),
        ...mapGetters('auth', ['facilities', 'user']),
        ...mapGetters('encounters', ['isSynced', 'genders', 'visitTypes']),
        ...mapState('encounters', { encounters: 'items' }),
        ...mapState('patients', { patients: 'items' }),
        isSuperUser() {
            return !!this.$authUser.user().is_superuser
        },
        defaultVisitDate() {
            return this.$date().format('YYYY-MM-DD')
        },
        isFiltersSet() {
            return !!this.search.facility && !!this.search.visit_date
        },
        facility() {
            return this.$store.getters['facilities/getById'](this.search.facility) || {}
        },
        sortedEncounters() {
            return this.$custom.sortItems(this.filteredEncounters.slice(), this.sort.by, this.sort.desc)
        },
        paginatedEncounters() {
            const start = (this.pagination.page - 1) * this.pagination.itemsPerPage
            const end = start + this.pagination.itemsPerPage
            return this.sortedEncounters.slice(start, end)
        },
        filterMessage() {
            return 'Your current filter returns too many records to display. Please narrow your search.'
        },
        pageCount() {
            return Math.ceil(this.filteredEncounters.length / this.pagination.itemsPerPage) || 1
        },
        page: {
            get() {
                const maxPage = Math.ceil(this.filteredEncounters.length / this.pagination.itemsPerPage) || 1
                if (this.pagination.page > maxPage) {
                    this.$nextTick(() => {
                        this.pagination.page = 1
                    })
                    return 1
                }
                return this.pagination.page
            },
            set(newValue) {
                this.pagination.page = newValue
            },
        },
        computedRules() {
            let rulesArray = []
            rulesArray = rulesArray.concat(this.$validationRules.requiredEmail, this.$validationRules.maxEmails(10))
            return rulesArray
        },
        mobileNumberRules() {
            return this.$validationRules.mobileNumberArrayRequired
        },
    },
    watch: {
        filteredEncounters: {
            handler(newVal) {
                this.$forceUpdate()
                if (Array.isArray(newVal) && newVal) {
                    const newPageCount = Math.ceil(newVal.length / this.pagination.itemsPerPage) || 1
                    this.pagination.pageCount = newPageCount
                    if (this.pagination.page > newPageCount) {
                        this.pagination.page = newPageCount
                    }
                }
            },
            deep: true,
            immediate: true,
        },
        'pagination.itemsPerPage': {
            handler(newVal) {
                const newPageCount = Math.ceil(this.filteredEncounters.length / newVal) || 1
                this.pagination.pageCount = newPageCount
                if (this.pagination.page > newPageCount) {
                    this.pagination.page = 1
                }
            },
        },
        'search.visit_date': {
            handler() {
                this.search.visit_date_us =
                    this.search.visit_date !== null ? this.$date(this.search.visit_date).format('MM/DD/YYYY') : null
                // Reset selection when visit_date changes
                this.selected = []
                this.updateSelectStatus()
            },
        },
        'search.facility': {
            handler() {
                // Reset selection when facility changes
                this.selected = []
                this.updateSelectStatus()
            },
        },
        search: {
            deep: true,
            handler() {
                this.handleSearchChange()
            },
        },
        pagination: {
            deep: true,
            handler() {
                this.updateQuery()
            },
        },
        sort: {
            deep: true,
            handler() {
                this.updateQuery()
            },
        },
    },
    async mounted() {
        this.$store.dispatch('loading', false)

        this.$nextTick().then(async () => {
            this.search = {
                ...initialSearch,
                visit_date: this.defaultVisitDate
            }
            await this.$nextTick()
        })
    },
    methods: {
        selectAllToggle() {
            if (this.selectAllStatus && !this.selectAllIndeterminate) {
                this.selected = this.paginatedEncounters.filter(x => x.is_signed)
            } else {
                this.selected = []
            }
            this.$nextTick(() => {
                this.updateSelectStatus()
            })
        },
        updateSelectStatus() {
            const selected = Array.isArray(this.selected) ? this.selected : []
            const filtered = Array.isArray(this.paginatedEncounters) ? this.paginatedEncounters : []

            const selectedCount = selected.length
            const filteredCount = filtered.length

            this.selectAllIndeterminate = selectedCount > 0 && selectedCount < filteredCount
            this.selectAllStatus = selectedCount > 0 && selectedCount === filteredCount

            this.isSelectedNewLog =
                selectedCount === 0 || selected.some(encounter => !encounter.communication_log_id)
        },

        openCommModal() {
            this.communicationLog.isPasswordVisible = false
            this.communicationLog.password = this.facility.password
            this.communicationLog.emailOptions =
                this.$store.getters['auth/getQuickEntryEmailsById'](this.facility?.id)?.email_addresses?.split(',') ||
                []
            this.communicationLog.emailOptions?.forEach((email, index) => {
                this.communicationLog.emailOptions[index] = email.trim()
            })
            this.communicationLog.recipients = [...this.communicationLog.emailOptions]

            const patients = []
            this.selected.forEach(encounter => {
                patients.push(`${encounter.first_name} ${encounter.last_name}`)
            })
            this.communicationLog.patients = patients.join(', ')

            this.commModalState = true
        },
        commitPendingEmailEntry() {
            const input = this.emailInput?.trim()
            if (input && !this.communicationLog.recipients.includes(input)) {
                this.communicationLog.recipients.push(input)
            }
            this.emailInput = ''
            if (this.$refs.emailCombo) {
                this.$refs.emailCombo.internalSearch = ''
            }
            this.recipientsChanged()
        },
        recipientsChanged() {
            let validEmail = true
            this.communicationLog.recipients.forEach((recipient, index) => {
                const emailPattern = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*\.[a-zA-Z]{2,}$/
                if (!emailPattern.test(recipient)) {
                    this.communicationLog.recipients.splice(index, 1)
                    validEmail = false
                }
            })
            if (!validEmail) {
                this.$store.dispatch('notify', { value: 'Invalid email address entered.', color: 'error' })
            }
        },
        generateNewPassword() {
            this.communicationLog.isPasswordVisible = false
            this.$root
                .confirm({
                    title: 'Generate New Communication Log Password?',
                    html: true,
                    body: `Are you sure you wish to generate a new communication log password for <b>${this.facility.title}</b>?<br><br>
                   Note: The new password won't be saved till a new communication log is sent and won't work for previous communication logs.<br><br>`,
                    confirm: 'Generate New Password',
                    confirmColor: 'error',
                    confirmIcon: this.icons.mdiLockReset,
                })
                .then(result => {
                    if (result) {
                        const validChars = '23456789abcdefghkmnprstuvwxyz'
                        let newPassword = ''
                        for (let i = 0; i < 8; i += 1) {
                            const randomNumber = Math.floor(
                                (crypto.getRandomValues(new Uint32Array(1))[0] / 0x100000000) * validChars.length,
                            )
                            newPassword += validChars.substring(randomNumber, randomNumber + 1)
                        }
                        this.communicationLog.isPasswordVisible = true
                        this.communicationLog.password = newPassword
                        this.$store.dispatch('notify', { value: 'New communication log password generated.' })
                    }
                })
        },
        async sendPasswordThroughSms() {
            const comboboxRef = this.$refs.mobileNumbersCombobox || this.$refs.mobileNumbersComboboxDetail
            if (comboboxRef) {
                comboboxRef.blur()
            }

            await this.$nextTick()

            if (comboboxRef && !comboboxRef.validate()) {
                return false
            }

            this.sendSmsPasswordBtnDisabled = true
            this.sendPasswordLabel = 'Sending Password through SMS'

            const foundCountry = this.countryCodes.find(country => country.id === this.selectedCountryCode)
            const payLoadMobileNumbers = []
            this.communicationLog.mobileNumbers.forEach(mobileNumber => {
                payLoadMobileNumbers.push(foundCountry.cleanValue + mobileNumber)
            })

            const password = this.communicationLog.password
                ? this.communicationLog.password
                : this.logDetails.password

            const payLoad = {
                to: payLoadMobileNumbers,
                text: `Your communication log password is: ${password}`,
            }

            await this.$store.dispatch('encounters/sendPasswordThroughSms', payLoad)
            this.sendSmsPasswordBtnDisabled = false
            this.sendPasswordLabel = 'Send Password'
        },

        async createCommunicationLog() {
            this.commModalState = false

            // Filter the selected encounter array
            let encountersToSync = this.selected.filter(
                encounter =>
                    encounter.is_signed &&
                    encounter.created_by_user_id === this.userId &&
                    encounter.place_of_service_id === this.search.facility &&
                    encounter.visit_date === this.search.visit_date &&
                    !encounter.is_deferred_care,
            )

            // Build patients to sync
            let patientsToSync = []
            let encounterIds = []
            encountersToSync.forEach(encounter => {
                delete encounter.can_be_deleted
                patientsToSync.push(this.$store.getters['patients/getById'](encounter.patient_id))
                encounterIds.push(encounter.id)
            })

            // Retrieve all unsynced encounters linked to the patient IDs
            patientsToSync.forEach(patient => {
                const otherEncounters = this.encounters.filter(
                    e => e.created_by_user_id === this.userId && !e.is_synced && e.patient_id === patient.id,
                )
                otherEncounters.forEach(otherEncounter => {
                    encountersToSync.push(otherEncounter)
                })
            })

            // De-dupe arrays
            encountersToSync = [...new Set(compact(encountersToSync))]
            patientsToSync = [...new Set(compact(patientsToSync))]
            encounterIds = [...new Set(compact(encounterIds))]

            // Create communication log object
            const commLog = {
                created_by_user_id: this.userId,
                place_of_service_id: this.search.facility,
                visit_date: this.search.visit_date,
                log_type: this.isSelectedNewLog ? 'Initial' : 'Update',
                password: this.communicationLog.password,
                contact_email: this.communicationLog.recipients.join(', '),
                encounter_ids: encounterIds,
            }

            await this.sendCommunicationLog(encountersToSync, patientsToSync, commLog)
        },
        async logCleanup() {
            await this.$store.dispatch('encounters/syncingModal', false)
            this.selected = []
            this.updateSelectStatus()
            this.filteredEncounters = []
            await this.loadFilteredEncounters()
        },

        onSortChange(value, type) {
            if (type === 'by') {
                this.sort = { ...this.sort, by: value }
            } else if (type === 'desc') {
                this.sort = { ...this.sort, desc: value }
            }
        },
        handleSearchChange() {
            clearTimeout(this.debounceTimer)
            this.debounceTimer = setTimeout(() => {
                this.loadFilteredEncounters()
                this.updateQuery()
            }, 250)
        },
        async loadFilteredEncounters() {
            this.loading = true
            try {
                this.encountersLength = 0
                this.filteredEncounters = []
                // Don't load anything if filters are not set (facility and visit_date)
                if (!this.isFiltersSet) {
                    this.loading = false
                    return
                }

                const filteredEncounters = this.$store.getters['encounters/filteredEncountersBySearch']({
                    ...this.search,
                    user_id: this.userId,
                    practice_type_id: 1, // Wound care practice type
                    is_signed: true, // Only signed encounters
                    omitDeferredCare: true, // do not include deferred care notes
                })

                this.encountersLength = filteredEncounters.length

                if (this.encountersLength >= 1000) {
                    this.filteredEncounters = []
                    return
                }

                this.filteredEncounters = []
                const batchSize = 200
                let results = []

                for (let i = 0; i < filteredEncounters.length; i += batchSize) {
                    const batch = filteredEncounters.slice(i, i + batchSize)
                    batch.forEach(encounter => {
                        const patient = this.getPatient(encounter.patient_id)
                        if (patient) {
                            Object.assign(encounter, {
                                first_name: patient.first_name,
                                last_name: patient.last_name,
                                gender: patient.gender,
                                ssn_last_4: patient.ssn_last_4,
                                dob: patient.dob,
                                is_deceased: patient.is_deceased,
                            })
                        }
                    })

                    results = results.concat(batch)
                    await new Promise(resolve => setTimeout(resolve, 0))
                }
                filteredEncounters.sort((a, b) => new Date(b.created) - new Date(a.created))
                this.filteredEncounters = [...results]
            } finally {
                this.loading = false
            }
        },
        updateQuery() {
            const query = { ...this.search, ...this.sort, ...this.pagination }
            Object.keys(query).forEach(key => {
                if (query[key] === null || typeof query[key] === 'undefined') query[key] = ''
            })
            window.history.replaceState({}, null, `${this.$route.path}?${new URLSearchParams(query).toString()}`)
            this.$store.commit('route/updateQuery', query)
            this.expanded = []
        },
        getPatient(patientId) {
            return this.$store.getters['patients/getById'](patientId)
        },
        getFacilityName(placeOfServiceId) {
            const facilityName = this.$store.getters['facilities/getById'](placeOfServiceId).title
            return facilityName || 'No facility found'
        },
        logStatusColor(communicationLog) {
            if (!communicationLog) return 'error'
            switch (communicationLog.log_status) {
                case 'Initiated': return 'warning'
                case 'Sent': return 'success'
                case 'Failed': return 'error'
                default: return 'error'
            }
        },
        logStatusIcon(communicationLog) {
            if (!communicationLog) return this.icons.mdiCloseCircle
            switch (communicationLog.log_status) {
                case 'Initiated': return this.icons.mdiEmailArrowRight
                case 'Sent': return this.icons.mdiEmailCheck
                case 'Failed': return this.icons.mdiEmailAlert
                default: return this.icons.mdiCloseCircle
            }
        },
    },
}
</script>

<style lang="scss" scoped>
.row-container {
    display: flex;
    align-items: center;
}

.country-code {
    margin-top: -11px !important;

    .v-select__slot {
        max-height: inherit !important;
    }

    .v-input__icon {
        height: 48px !important;
    }
}
</style>
